import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@/components/base/Card';
import Badge from '@/components/base/Badge';
import Tabs from '@/components/base/Tabs';
import StatusBadge from '@/components/base/StatusBadge';
import ProgressBar from '@/components/base/ProgressBar';
import EmptyState from '@/components/base/EmptyState';
import { workflowStages } from '@/types';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

const stageTone: Record<string, string> = {
  Bekliyor: 'bg-secondary-200 text-secondary-600',
  'Devam Ediyor': 'bg-primary-500 text-white',
  Tamamlandı: 'bg-primary-100 text-primary-700',
  Bloke: 'bg-accent-500 text-white',
};

const discussions = [
  { date: '2026-08-10', author: 'Murat Yılmaz', text: 'Müşteri ile haftalık durum toplantısı yapıldı, montaj tarihi teyit edildi.' },
  { date: '2026-08-03', author: 'Fatih Demir', text: 'Teknik şartname revizyonu üzerinde mutabık kalındı.' },
  { date: '2026-07-22', author: 'Murat Yılmaz', text: 'Kapora ödemesi alındı, proje resmi olarak başlatıldı.' },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();
  const { projects, tasks, purchases, serviceRecords, files, collections, projectFinances, customerName } = useData();
  const project = projects.find((p) => p.id === id);
  const [tab, setTab] = useState('genel');
  const [noteStage, setNoteStage] = useState<string | null>(null);

  if (!project) {
    return (
      <div className="animate-fade-in-up">
        <EmptyState icon="ri-folder-open-line" title="Proje bulunamadı" description="Aradığınız kayıt mevcut değil." />
        <div className="mt-4 text-center">
          <button type="button" onClick={() => navigate('/projects')} className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">
            Proje Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  const remaining = project.salesAmount - project.collected;
  const profit = project.salesAmount - project.cost;
  const currentIndex = workflowStages.indexOf(project.currentStage as (typeof workflowStages)[number]);

  const stageState = (stage: string, idx: number) => {
    if (project.blockedStage === stage) return 'Bloke';
    if (project.status === 'Tamamlandı') return 'Tamamlandı';
    if (idx < currentIndex) return 'Tamamlandı';
    if (idx === currentIndex) return 'Devam Ediyor';
    return 'Bekliyor';
  };

  const projectTasks = tasks.filter((t) => t.projectId === project.id);
  const projectPurchases = purchases.filter((pu) => pu.projectId === project.id);
  const projectFiles = files.filter((f) => f.relatedType === 'Proje' && f.relatedId === project.id);
  const projectServices = serviceRecords.filter((s) => s.projectId === project.id);
  const projectCollections = collections.filter((c) => c.projectId === project.id);
  const finance = projectFinances.find((pf) => pf.projectId === project.id);

  const tabs = [
    { key: 'genel', label: 'Genel' },
    { key: 'akis', label: 'İş Akışı' },
    { key: 'gorevler', label: 'Görevler' },
    { key: 'teknik', label: 'Teknik Bilgiler' },
    { key: 'malzemeler', label: 'Malzemeler' },
    { key: 'satin', label: 'Satın Alma' },
    { key: 'finans', label: 'Finans' },
    { key: 'dosyalar', label: 'Dosyalar' },
    { key: 'görüşmeler', label: 'Görüşmeler' },
    { key: 'servis', label: 'Servis Geçmişi' },
  ];

  return (
    <div className="space-y-5 animate-fade-in-up">
      <button type="button" onClick={() => navigate('/projects')} className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-foreground-500 transition-colors hover:text-foreground-900">
        <i className="ri-arrow-left-line" /> Projeler
      </button>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-semibold text-foreground-950">{project.name}</h1>
              <StatusBadge status={project.status} />
            </div>
            <p className="mt-1 text-sm text-foreground-500">{project.id} · {project.category}</p>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5"><i className="ri-team-line" /> {customerName(project.customerId)}</span>
              <span className="flex items-center gap-1.5"><i className="ri-user-3-line" /> {project.responsible}</span>
              <span className="flex items-center gap-1.5"><i className="ri-calendar-line" /> {formatDate(project.startDate)} → {formatDate(project.plannedDelivery)}</span>
            </div>
          </div>
          <div className="w-full lg:w-64">
            <div className="flex items-center justify-between text-xs text-foreground-500">
              <span>Genel İlerleme</span>
              <span className="font-semibold text-foreground-900">%{project.progress}</span>
            </div>
            <ProgressBar value={project.progress} className="mt-1.5" />
            <p className="mt-2 text-sm text-foreground-500">Mevcut Aşama: <span className="font-semibold text-foreground-900">{project.currentStage}</span></p>
          </div>
        </div>
      </div>

      <Tabs items={tabs} value={tab} onChange={setTab} />

      {tab === 'genel' && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Proje Açıklaması</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{project.description}</p>
            <div className="mt-5 grid grid-cols-2 gap-4 border-t border-background-200 pt-5 text-sm sm:grid-cols-3">
              <div><p className="text-xs text-foreground-500">Başlangıç</p><p className="font-semibold text-foreground-900">{formatDate(project.startDate)}</p></div>
              <div><p className="text-xs text-foreground-500">Planlanan Teslim</p><p className="font-semibold text-foreground-900">{formatDate(project.plannedDelivery)}</p></div>
              <div><p className="text-xs text-foreground-500">İş Kategorisi</p><p className="font-semibold text-foreground-900">{project.category}</p></div>
            </div>
          </Card>
          <Card className="p-5">
            <h2 className="font-heading text-base font-semibold text-foreground-950">Finansal Özet</h2>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-foreground-500">Satış Tutarı</span><span className="font-semibold text-foreground-900">{formatTL(project.salesAmount)}</span></div>
              <div className="flex justify-between"><span className="text-foreground-500">Tahsil Edilen</span><span className="font-semibold text-foreground-900">{formatTL(project.collected)}</span></div>
              <div className="flex justify-between"><span className="text-foreground-500">Kalan Alacak</span><span className="font-semibold text-accent-700">{remaining > 0 ? formatTL(remaining) : '-'}</span></div>
              <div className="flex justify-between"><span className="text-foreground-500">Proje Maliyeti</span><span className="font-semibold text-foreground-900">{formatTL(project.cost)}</span></div>
              <div className="flex justify-between border-t border-background-200 pt-3"><span className="text-foreground-500">Tahmini Kâr</span><span className="font-semibold text-primary-700">{formatTL(profit)}</span></div>
            </div>
          </Card>
        </div>
      )}

      {tab === 'akis' && (
        <Card className="p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-950">İş Akışı</h2>
          <div className="relative mt-5 border-l-2 border-background-200 pl-8">
            {workflowStages.map((stage, idx) => {
              const state = stageState(stage, idx);
              return (
                <div key={stage} className="relative pb-5 last:pb-0">
                  <span className={`absolute -left-[43px] flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${stageTone[state]}`}>
                    {state === 'Tamamlandı' ? <i className="ri-check-line" /> : state === 'Bloke' ? <i className="ri-error-warning-line" /> : idx + 1}
                  </span>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{stage}</p>
                      <p className="text-xs text-foreground-500">{state}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={state} />
                      <button type="button" onClick={() => setNoteStage(noteStage === stage ? null : stage)} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 hover:text-foreground-700">
                        <i className="ri-sticky-note-line" />
                      </button>
                    </div>
                  </div>
                  {noteStage === stage && (
                    <div className="mt-3 rounded-md border border-background-200 bg-background-100/60 p-3">
                      <textarea placeholder={`"${stage}" aşamasına not ekleyin...`} className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400" rows={2} />
                      <div className="mt-2 flex items-center justify-between">
                        <button type="button" onClick={() => showToast('Dosya ekleme hazırlanıyor')} className="cursor-pointer text-xs font-semibold text-foreground-500 hover:text-foreground-700">
                          <i className="ri-attachment-line" /> Dosya Ekle
                        </button>
                        <button type="button" onClick={() => { showToast('Aşama notu kaydedildi'); setNoteStage(null); }} className="cursor-pointer rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600">
                          Kaydet
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {tab === 'gorevler' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {projectTasks.length === 0 ? <EmptyState title="Görev bulunamadı" /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Görev</th><th className="px-4 py-3">Sorumlu</th><th className="px-4 py-3">Son Tarih</th><th className="px-4 py-3">Öncelik</th><th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {projectTasks.map((t) => (
                    <tr key={t.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 font-medium text-foreground-900">{t.title}</td>
                      <td className="px-4 py-3 text-foreground-700">{t.assignee}</td>
                      <td className="px-4 py-3 text-foreground-600">{formatDate(t.dueDate)}</td>
                      <td className="px-4 py-3"><Badge tone={t.priority === 'Kritik' ? 'accent' : t.priority === 'Önemli' ? 'accent' : 'neutral'}>{t.priority}</Badge></td>
                      <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'teknik' && (
        <Card className="p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-950">Teknik Bilgiler</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Besleme Gerilimi', value: '400V AC 3 Faz' },
              { label: 'Kontrol Gerilimi', value: '24V DC' },
              { label: 'Koruma Sınıfı', value: 'IP54' },
              { label: 'Haberleşme', value: 'Profinet / Modbus TCP' },
              { label: 'PLC Platformu', value: 'Siemens S7-1500' },
              { label: 'HMI', value: '15" Dokunmatik Panel' },
            ].map((spec) => (
              <div key={spec.label} className="rounded-md bg-background-100 p-3">
                <p className="text-xs text-foreground-500">{spec.label}</p>
                <p className="mt-0.5 font-semibold text-foreground-900">{spec.value}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === 'malzemeler' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {projectPurchases.length === 0 ? <EmptyState title="Malzeme bulunamadı" /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Malzeme</th><th className="px-4 py-3">Marka / Model</th><th className="px-4 py-3 text-center">Adet</th><th className="px-4 py-3 text-right">Birim</th><th className="px-4 py-3 text-right">Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {projectPurchases.map((pu) => (
                    <tr key={pu.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 font-medium text-foreground-900">{pu.material}</td>
                      <td className="px-4 py-3 text-foreground-700">{pu.brandModel}</td>
                      <td className="px-4 py-3 text-center text-foreground-700">{pu.quantity}</td>
                      <td className="px-4 py-3 text-right text-foreground-700">{formatTL(pu.unitPrice)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-900">{formatTL(pu.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'satin' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {projectPurchases.length === 0 ? <EmptyState title="Satın alma kaydı yok" /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Tedarikçi</th><th className="px-4 py-3">Malzeme</th><th className="px-4 py-3 text-right">Toplam</th><th className="px-4 py-3">Ödeme</th><th className="px-4 py-3">Malzeme Durumu</th>
                  </tr>
                </thead>
                <tbody>
                  {projectPurchases.map((pu) => (
                    <tr key={pu.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 font-medium text-foreground-900">{pu.supplier}</td>
                      <td className="px-4 py-3 text-foreground-700">{pu.material}</td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-900">{formatTL(pu.total)}</td>
                      <td className="px-4 py-3"><StatusBadge status={pu.paymentStatus} /></td>
                      <td className="px-4 py-3"><StatusBadge status={pu.materialStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'finans' && (
        <div className="space-y-5">
          {finance && (
            <Card className="p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">Proje Finans Analizi</h2>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
                <div><p className="text-xs text-foreground-500">Malzeme Maliyeti</p><p className="font-semibold text-foreground-900">{formatTL(finance.materialCost)}</p></div>
                <div><p className="text-xs text-foreground-500">Diğer Masraflar</p><p className="font-semibold text-foreground-900">{formatTL(finance.otherExpenses)}</p></div>
                <div><p className="text-xs text-foreground-500">Toplam Maliyet</p><p className="font-semibold text-foreground-900">{formatTL(finance.totalCost)}</p></div>
                <div><p className="text-xs text-foreground-500">Brüt Kâr</p><p className="font-semibold text-primary-700">{formatTL(finance.grossProfit)} (%{finance.profitRate})</p></div>
              </div>
            </Card>
          )}
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="border-b border-background-200 px-5 py-3 text-sm font-semibold text-foreground-900">Tahsilatlar</div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Tarih</th><th className="px-4 py-3">Tip</th><th className="px-4 py-3 text-right">Tutar</th><th className="px-4 py-3">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {projectCollections.length === 0 ? (
                    <tr><td colSpan={4} className="px-5 py-6 text-center text-foreground-500">Tahsilat kaydı yok</td></tr>
                  ) : projectCollections.map((c) => (
                    <tr key={c.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 text-foreground-600">{formatDate(c.date)}</td>
                      <td className="px-4 py-3"><Badge>{c.paymentType}</Badge></td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-900">{formatTL(c.amount)}</td>
                      <td className="px-4 py-3 text-foreground-600">{c.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'dosyalar' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {projectFiles.length === 0 ? <EmptyState title="Dosya bulunamadı" /> : projectFiles.map((f) => (
            <Card key={f.id} className="flex items-center gap-3 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-file-3-line" /></span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground-900">{f.name}</p>
                <p className="text-xs text-foreground-500">{f.category} · {f.size}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'görüşmeler' && (
        <div className="space-y-3">
          {discussions.map((d, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">{d.author.slice(0, 1)}</span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground-900">{d.author}</p>
                    <span className="text-xs text-foreground-400">{formatDate(d.date)}</span>
                  </div>
                  <p className="mt-1 text-sm text-foreground-700">{d.text}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'servis' && (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {projectServices.length === 0 ? <EmptyState title="Servis kaydı bulunamadı" /> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-background-200 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-5 py-3">Talep</th><th className="px-4 py-3">Sorun</th><th className="px-4 py-3">Garanti</th><th className="px-4 py-3">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {projectServices.map((s) => (
                    <tr key={s.id} className="border-b border-background-100 last:border-0">
                      <td className="px-5 py-3 text-foreground-600">{formatDate(s.requestDate)}</td>
                      <td className="px-4 py-3 text-foreground-800">{s.issue}</td>
                      <td className="px-4 py-3"><StatusBadge status={s.warranty} /></td>
                      <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}